import { word } from "@modules/core/utils/i18n"
import { PublicProfileInfoResume } from "@modules/profile/publicProfile/components/publicProfileInfoResume"
import { ProgramInfinite, ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import {
  useProgramSubscriberDetails,
  UseProgramSubscriberDetailsKey,
} from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuAvatarCardSidebar } from "@modules/ui/components/huAvatarCardSidebar"
import { HUText } from "@modules/ui/components/huText"
import { useMemo } from "react"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { BoxesIcon } from "lucide-react"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useTheme } from "styled-components"

type ProgramSubscriberDetailsHeaderProps = {
  program: ProgramInfinite | ProgramOnOff | ProgramBundleEntity
  subscriberId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsHeader({
  program,
  subscriberId,
  className,
  ...props
}: ProgramSubscriberDetailsHeaderProps) {
  const { data: subscriberDetails } = useProgramSubscriberDetails(
    program instanceof ProgramBundleEntity
      ? UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER
      : UseProgramSubscriberDetailsKey.PROGRAM_SUBSCRIBER,
    program.id,
    subscriberId,
  )
  const { sidebarNavigateTo } = useSidebar()
  const theme = useTheme()

  const openUserProfile = () => {
    sidebarNavigateTo({
      content: <PublicProfileInfoResume userId={subscriberDetails.id} />,
      onHide: () => {
        return Promise.resolve(true)
      },
    })
  }

  const itemIcon = useMemo(() => {
    if (program instanceof ProgramBundleEntity) {
      return <BoxesIcon color={theme.neutralColor500} width={15} height={15} />
    } else if (program._programType === ProgramInfiniteTypeOf) {
      return <InfinityIcon color={theme.neutralColor500} width={15} height={15} />
    } else return <OnOffIcon color={theme.neutralColor500} width={15} height={15} />
  }, [program])

  return (
    <HuAvatarCardSidebar
      id={subscriberDetails.id}
      avatar={subscriberDetails.avatar}
      firstname={subscriberDetails.firstname}
      lastname={subscriberDetails.lastname}
      username={subscriberDetails.username}
      prefixUsername={subscriberDetails.username ? "@" : undefined}
      nationality={subscriberDetails.nationality}
      email={subscriberDetails.email}
      onAvatarClick={openUserProfile}
      isLoading={false}
      className={className}
      {...props}
      topContent={
        <div className="flex flex-column align-items-center pt-2">
          {!(program instanceof ProgramBundleEntity) && program._programType === ProgramOnOffTypeOf ? (
            <HUText fontStyle="TM">{word("program.label.athlete")}</HUText>
          ) : (
            <HUText fontStyle="TM">{word("program.label.mixed_subscribers")}</HUText>
          )}

          <HUText fontStyle="LM" color={theme.neutralColor500} className="capitalize flex gap-1 align-items-center">
            {itemIcon}
            {program instanceof ProgramBundleEntity ? program.name : program.title}
          </HUText>
        </div>
      }
    />
  )
}
